import { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../components/atoms/Toaster'
import { useAlerts } from './useAlerts'
import { useEmails } from './useEmails'
import type { Alert, Email } from '../types'

const POLL_MS = 30_000
const EMAIL_BATCH_WINDOW_MS = 4_000
const MAX_EMAILS_IN_BATCH_TOAST = 3
const SEEN_STORAGE_KEY = 'torre.seenNotifications.v1'
const MAX_SEEN_IDS = 500

interface SeenState {
  emails: string[]
  alerts: string[]
}

function loadSeen(): SeenState {
  if (typeof window === 'undefined') return { emails: [], alerts: [] }
  try {
    const raw = window.sessionStorage.getItem(SEEN_STORAGE_KEY)
    if (!raw) return { emails: [], alerts: [] }
    const parsed = JSON.parse(raw)
    return {
      emails: Array.isArray(parsed.emails) ? parsed.emails : [],
      alerts: Array.isArray(parsed.alerts) ? parsed.alerts : [],
    }
  } catch {
    return { emails: [], alerts: [] }
  }
}

function saveSeen(state: SeenState) {
  if (typeof window === 'undefined') return
  try {
    const trimmed: SeenState = {
      emails: state.emails.slice(-MAX_SEEN_IDS),
      alerts: state.alerts.slice(-MAX_SEEN_IDS),
    }
    window.sessionStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(trimmed))
  } catch {
    // sessionStorage lleno o no disponible, ignorar
  }
}

function severityName(alert: Alert): string {
  if (typeof alert.severity === 'string') return alert.severity.toLowerCase()
  return (alert.severity?.name || '').toLowerCase()
}

function statusName(alert: Alert): string {
  return (alert.status || '').toLowerCase()
}

function isCriticalAlert(alert: Alert): boolean {
  if (statusName(alert) === 'resolved' || statusName(alert) === 'acknowledged') {
    return false
  }
  const sev = severityName(alert)
  return sev === 'critical' || sev === 'critico' || sev === 'crítico'
}

function truncate(text: string | undefined | null, max: number): string {
  if (!text) return ''
  const clean = text.replace(/\s+/g, ' ').trim()
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean
}

/**
 * Hook que muestra toasts cuando llegan correos nuevos o se generan alertas críticas.
 *
 * - Polling cada 30s (mismo intervalo que useEmails/useAlerts)
 * - Persiste IDs vistos en sessionStorage para no duplicar toasts al refrescar
 * - Agrupa emails nuevos en un solo toast resumen si llegan varios a la vez
 * - Solo alerta las alertas de severidad "critical" activas
 * - Click en el toast navega al detalle
 */
export function useNotificationToasts(): void {
  const { addToast } = useToast()
  const navigate = useNavigate()

  const { data: emailsData } = useEmails({ limit: 20 })
  const { data: alertsData } = useAlerts({ status: 'open' })

  const seenRef = useRef<SeenState>({ emails: [], alerts: [] })
  const primedRef = useRef<boolean>(false)
  const lastBatchTimerRef = useRef<number | null>(null)
  const pendingEmailsRef = useRef<Email[]>([])

  useEffect(() => {
    seenRef.current = loadSeen()
  }, [])

  const flushEmailBatch = useCallback(() => {
    const pending = pendingEmailsRef.current
    pendingEmailsRef.current = []
    if (pending.length === 0) return

    const total = pending.length
    const sample = pending.slice(0, MAX_EMAILS_IN_BATCH_TOAST)
    const sampleText = sample
      .map((e) => `• ${truncate(e.subject, 50)}`)
      .join('\n')
    const extra = total > MAX_EMAILS_IN_BATCH_TOAST ? `\n…y ${total - MAX_EMAILS_IN_BATCH_TOAST} más` : ''

    const firstId = pending[0].id
    addToast({
      type: 'info',
      title: total === 1 ? 'Nuevo correo recibido' : `${total} correos nuevos`,
      message: total === 1 ? truncate(pending[0].subject, 80) : `${sampleText}${extra}`,
      duration: 7000,
      action: {
        label: total === 1 ? 'Ver correo' : 'Ver correos',
        onClick: () => navigate(`/emails/${firstId}`),
      },
    })
  }, [addToast, navigate])

  useEffect(() => {
    const emails: Email[] = emailsData?.data ?? []
    if (emails.length === 0) return

    const seenSet = new Set(seenRef.current.emails)
    const newIds: string[] = []

    for (const email of emails) {
      if (!seenSet.has(email.id)) newIds.push(email.id)
    }

    if (!primedRef.current) {
      primedRef.current = true
      const allIds = emails.map((e) => e.id)
      const merged = Array.from(new Set([...seenRef.current.emails, ...allIds]))
      seenRef.current.emails = merged
      saveSeen(seenRef.current)
      return
    }

    if (newIds.length === 0) return

    const newEmails = emails.filter((e) => newIds.includes(e.id))
    seenRef.current.emails = Array.from(
      new Set([...seenRef.current.emails, ...newIds]),
    )
    saveSeen(seenRef.current)

    pendingEmailsRef.current.push(...newEmails)
    if (lastBatchTimerRef.current !== null) {
      window.clearTimeout(lastBatchTimerRef.current)
    }
    lastBatchTimerRef.current = window.setTimeout(() => {
      flushEmailBatch()
      lastBatchTimerRef.current = null
    }, EMAIL_BATCH_WINDOW_MS)
  }, [emailsData, flushEmailBatch])

  useEffect(() => {
    return () => {
      if (lastBatchTimerRef.current !== null) {
        window.clearTimeout(lastBatchTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const alerts: Alert[] = alertsData?.data ?? []
    if (alerts.length === 0) return

    const seenSet = new Set(seenRef.current.alerts)

    if (!primedRef.current) {
      const allIds = alerts.map((a) => a.id)
      const merged = Array.from(new Set([...seenRef.current.alerts, ...allIds]))
      seenRef.current.alerts = merged
      saveSeen(seenRef.current)
      return
    }

    const newCritical: Alert[] = []
    for (const alert of alerts) {
      if (seenSet.has(alert.id)) continue
      if (!isCriticalAlert(alert)) continue
      newCritical.push(alert)
    }

    if (newCritical.length === 0) {
      const allIds = alerts.map((a) => a.id)
      seenRef.current.alerts = Array.from(
        new Set([...seenRef.current.alerts, ...allIds]),
      )
      saveSeen(seenRef.current)
      return
    }

    seenRef.current.alerts = Array.from(
      new Set([...seenRef.current.alerts, ...newCritical.map((a) => a.id)]),
    )
    saveSeen(seenRef.current)

    for (const alert of newCritical) {
      addToast({
        type: 'error',
        title: 'Alerta crítica',
        message: truncate(alert.title, 100),
        duration: 9000,
        action: {
          label: 'Ver alerta',
          onClick: () => navigate(`/alerts/${alert.id}`),
        },
      })
    }
  }, [alertsData, addToast, navigate])
}
